import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { State } from '../../types/state'
import { useRequests } from '../../requests/useRequests'
import { PageBanner } from '../Banners/PageBanner'
import { Designer } from '../../types/designer'
import Header from '../Header/Header'
import Footer from '../Footer/Footer'

const TailwindSpinner: React.FC<{ className?: string }> = ({ className = 'tw-h-4 tw-w-4' }) => (
  <div
    className={`tw-animate-spin tw-rounded-full tw-border-2 tw-border-solid tw-border-current tw-border-r-transparent ${className}`}
    role='status'
  >
    <span className='tw-sr-only'>Ładowanie...</span>
  </div>
)

const DesignerDetails = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [designer, setDesigner] = useState<Designer | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [showEmail, setShowEmail] = useState<boolean>(false)
  const [showAddress, setShowAddress] = useState<boolean>(false)
  const [showPhoneNumber, setShowPhoneNumber] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [address, setAddress] = useState('')
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [loadingEmail, setLoadingEmail] = useState<boolean>(false)
  const [loadingPhone, setLoadingPhone] = useState<boolean>(false)
  const [loadingAddress, setLoadingAddress] = useState<boolean>(false)
  const requests = useRequests()

  const { id: userId } = useSelector((state: State) => state.user)
  const isOwner = userId && designer?.userId === userId
  const visitTracked = useRef(false)

  useEffect(() => {
    const fetchDesigner = async () => {
      setLoading(true)
      try {
        const response = await requests.getDesigner(id!)
        setDesigner(response.data)
      } catch (err: any) {
        if (err.response?.status === 404) {
          setError('Nie znaleziono projektanta')
        } else {
          setError('Nie udało się pobrać danych projektanta')
        }
        console.error(err)
      } finally {
        setLoading(false)
      }
    }
    if (id) fetchDesigner()
  }, [id, requests])

  useEffect(() => {
    if (visitTracked.current || !id || loading || (designer == null)) return
    const trackVisit = async () => {
      try {
        await requests.trackDesignerProfileVisit(id)
        visitTracked.current = true
      } catch (error) {
        console.error('Nie udało się zarejestrować wizyty:', error)
      }
    }
    trackVisit()
  }, [id, designer, loading, requests])

  useEffect(() => {
    if (designer?.user?.phoneNumber) setPhoneNumber(designer.user.phoneNumber)
  }, [designer])

  const handleEditProfile = () => navigate('/designer/edit')

  const handleContactClick = async (type: 'phone' | 'email' | 'address') => {
    if (type === 'phone') setLoadingPhone(true)
    else if (type === 'email') setLoadingEmail(true)
    else if (type === 'address') setLoadingAddress(true)

    try {
      if (designer?.id) await requests.trackDesignerContactClick(designer.id, type)
    } catch (error) {
      console.error(`Nie udało się zarejestrować kliknięcia ${type}:`, error)
    }

    if (type === 'phone') {
      setShowPhoneNumber(true)
      setLoadingPhone(false)
    } else if (type === 'email') {
      setShowEmail(true)
      setLoadingEmail(false)
    } else if (type === 'address') {
      if ((designer?.user) != null) {
        const user = designer.user
        const addressParts = [user.street, user.zipCode, user.city].filter(Boolean)
        setAddress(addressParts.join(', ') || '')
      }
      setShowAddress(true)
      setLoadingAddress(false)
    }
  }

  const handleWebsiteClick = async () => {
    try {
      if (designer?.id) await requests.trackDesignerContactClick(designer.id, 'website')
    } catch (error) {
      console.error('Nie udało się zarejestrować kliknięcia website:', error)
    }
  }

  const openLightbox = (imageUrl: string) => setSelectedImage(imageUrl)
  const closeLightbox = () => setSelectedImage(null)

  if (loading) {
    return (
      <div className='loading-container'>
        <div className='spinner-border text-primary' role='status'>
          <span className='sr-only'>Ładowanie...</span>
        </div>
        <p className='mt-3'>Ładowanie...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className='tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-screen tw-py-12 tw-px-4'>
        <div className='tw-bg-red-100 tw-border tw-border-red-400 tw-text-red-700 tw-px-6 tw-py-4 tw-rounded-md tw-text-center tw-shadow-md'>
          <p className='tw-font-semibold'>{error}</p>
          <button
            className='tw-mt-6 tw-px-6 tw-py-2 tw-bg-red-600 tw-text-white tw-font-semibold tw-rounded-md hover:tw-bg-red-700 tw-transition-colors'
            onClick={() => navigate('/designers')}
          >
            Powrót do listy projektantów
          </button>
        </div>
      </div>
    )
  }

  if (designer == null) {
    return (
      <div className='tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-screen tw-py-12 tw-px-4'>
        <div className='tw-bg-yellow-100 tw-border tw-border-yellow-400 tw-text-yellow-700 tw-px-6 tw-py-4 tw-rounded-md tw-text-center tw-shadow-md'>
          <h2 className='tw-font-semibold'>Nie znaleziono projektanta</h2>
          <button
            className='tw-mt-6 tw-px-6 tw-py-2 tw-bg-yellow-500 tw-text-white tw-font-semibold tw-rounded-md hover:tw-bg-yellow-600 tw-transition-colors'
            onClick={() => navigate('/designers')}
          >
            Powrót do listy projektantów
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      <Header />
      <PageBanner
        title={designer.name}
        description={designer.shortDescription}
        backgroundImage={designer.backgroundImagePresignedUrl || designer.backgroundImage}
        avatarImage={designer.imagePresignedUrl || designer.image || '/assets/images/designers/placeholder.jpg'}
        avatarAlt={designer.name}
      />
      <section className='designer-details tw-py-12 md:tw-py-16 lg:tw-py-20'>
        <div className='tw-container tw-mx-auto tw-px-4'>
          <div className='tw-mb-10 md:tw-mb-12'>
            <h2 className='tw-text-3xl lg:tw-text-4xl tw-font-bold tw-text-gray-900 tw-mb-1'>{designer.name}</h2>
            <p className='tw-text-lg lg:tw-text-xl tw-text-gray-600 tw-mb-4'>
              {designer.types.map(type => {
                switch (type) {
                  case 'interior_design': return 'Projektowanie wnętrz'
                  case 'construction_design': return 'Projektowanie konstrukcji'
                  case 'garden_design': return 'Projektowanie ogrodów'
                  case 'design': return 'Projektowanie'
                  case 'other_services': return 'Pozostałe usługi'
                  case 'interior': return 'Projektowanie wnętrz'
                  case 'other': return 'Pozostałe usługi'
                  case 'architect': return 'Architekt'
                  default: return type
                }
              }).join(', ')}
            </p>
            <p className='tw-text-gray-700 tw-leading-relaxed tw-mb-6'>{designer.shortDescription}</p>

            <div className='tw-border-t tw-border-gray-200 tw-pt-6'>
              <h3 className='tw-text-xl tw-font-semibold tw-text-gray-800 tw-mb-4'>Dane kontaktowe</h3>

              <div className='tw-space-y-3'>
                <div className='tw-flex tw-items-start'>
                  <i className='fas fa-envelope tw-text-gray-500 tw-w-5 tw-text-center tw-mr-3 tw-mt-1' />
                  <div className='tw-text-sm tw-text-gray-700'>
                    {showEmail
                      ? <span>{designer?.user?.email || 'Brak adresu email'}</span>
                      : (
                          <button
                            className='tw-font-semibold tw-text-blue-600 hover:tw-text-blue-800 focus:tw-text-blue-800 hover:tw-underline disabled:tw-opacity-50 disabled:tw-no-underline'
                            onClick={async () => await handleContactClick('email')}
                            disabled={loadingEmail}
                          >
                            {loadingEmail ? <><TailwindSpinner className='tw-mr-1.5 tw-h-3.5 tw-w-3.5 tw-inline' />Ładowanie...</> : 'Pokaż adres email'}
                          </button>
                        )}
                  </div>
                </div>

                <div className='tw-flex tw-items-start'>
                  <i className='fas fa-phone tw-text-gray-500 tw-w-5 tw-text-center tw-mr-3 tw-mt-1' />
                  <div className='tw-text-sm tw-text-gray-700'>
                    {showPhoneNumber
                      ? <span>{phoneNumber || 'Brak numeru telefonu'}</span>
                      : (
                          <button
                            className='tw-font-semibold tw-text-blue-600 hover:tw-text-blue-800 focus:tw-text-blue-800 hover:tw-underline disabled:tw-opacity-50 disabled:tw-no-underline'
                            onClick={async () => await handleContactClick('phone')}
                            disabled={loadingPhone}
                          >
                            {loadingPhone ? <><TailwindSpinner className='tw-mr-1.5 tw-h-3.5 tw-w-3.5 tw-inline' />Ładowanie...</> : 'Pokaż numer telefonu'}
                          </button>
                        )}
                  </div>
                </div>

                <div className='tw-flex tw-items-start'>
                  <i className='fas fa-map-marker-alt tw-text-gray-500 tw-w-5 tw-text-center tw-mr-3 tw-mt-1' />
                  <div className='tw-text-sm tw-text-gray-700'>
                    {showAddress
                      ? <span>{address || 'Brak adresu'}</span>
                      : (
                          <button
                            className='tw-font-semibold tw-text-blue-600 hover:tw-text-blue-800 focus:tw-text-blue-800 hover:tw-underline disabled:tw-opacity-50 disabled:tw-no-underline'
                            onClick={async () => await handleContactClick('address')}
                            disabled={loadingAddress}
                          >
                            {loadingAddress ? <><TailwindSpinner className='tw-mr-1.5 tw-h-3.5 tw-w-3.5 tw-inline' />Ładowanie...</> : 'Pokaż adres'}
                          </button>
                        )}
                  </div>
                </div>

                {designer?.website && (
                  <div className='tw-flex tw-items-start'>
                    <i className='fas fa-globe tw-text-gray-500 tw-w-5 tw-text-center tw-mr-3 tw-mt-1' />
                    <a
                      href={designer.website.startsWith('http') ? designer.website : `https://${designer.website}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      onClick={handleWebsiteClick}
                      className='tw-text-sm tw-text-blue-600 hover:tw-text-blue-800 hover:tw-underline break-all'
                    >
                      {designer.website}
                    </a>
                  </div>
                )}
              </div>
            </div>

            {isOwner && (
              <button
                className='tw-mt-8 md:tw-w-auto tw-w-full tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors tw-px-6 tw-py-2.5 lg:tw-text-base tw-bg-dark-teal tw-text-white hover:tw-bg-dark-teal/80 focus:tw-outline-none'
                onClick={handleEditProfile}
              >
                Edytuj profil
              </button>
            )}
          </div>

          {/* SEKCJA "O MNIE" I "PORTFOLIO" - bez zmian strukturalnych */}
          <div className='tw-w-full'>
            <div> {/* Usunięto klasę 'designer-details__content' jeśli niepotrzebna globalnie */}
              <h3 className='tw-text-2xl md:tw-text-3xl tw-font-bold tw-text-gray-800 tw-mb-4'>O mnie</h3>
              <p className='tw-text-gray-700 tw-leading-relaxed tw-mb-10 md:tw-mb-12'>{designer.description || 'Brak opisu.'}</p>

              {designer.portfolio && designer.portfolio.length > 0 && (
                <>
                  <h3 className='tw-text-2xl md:tw-text-3xl tw-font-bold tw-text-gray-800 tw-mb-6'>Portfolio</h3>
                  <div className='tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-6'>
                    {designer.portfolio.map((project, index) => {
                      const imageUrl = designer.portfolioPresignedUrls?.[index] || project
                      return imageUrl
                        ? (
                            <div
                              key={index}
                              className='tw-bg-white tw-rounded-lg tw-shadow-md tw-overflow-hidden hover:tw-shadow-xl tw-transition-shadow tw-duration-300'
                              onClick={() => openLightbox(imageUrl)}
                              style={{ cursor: 'pointer' }}
                            >
                              <img
                                src={imageUrl}
                                alt={`Projekt ${index + 1}`}
                                className='tw-w-full tw-h-56 tw-object-cover'
                              />
                            </div>
                          )
                        : null
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {selectedImage && (
        <div
          className='tw-fixed tw-inset-0 tw-bg-black/90 tw-flex tw-justify-center tw-items-center tw-z-[1050]'
          onClick={closeLightbox}
          style={{ cursor: 'pointer' }}
        >
          <div
            className='tw-relative tw-max-w-[90%] tw-max-h-[90%]'
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage}
              alt='Powiększone zdjęcie'
              className='tw-max-w-full tw-max-h-[90vh]'
              style={{ objectFit: 'contain' }}
            />
            <button
              onClick={closeLightbox}
              className='tw-absolute tw-top-[-40px] tw-right-0 tw-bg-transparent tw-border-none tw-text-white tw-text-2xl hover:tw-text-gray-300'
              style={{ cursor: 'pointer' }}
            >
              <i className='fas fa-times' />
            </button>
          </div>
        </div>
      )}
      <Footer />
    </>
  )
}

export default DesignerDetails
